import useMountEffect from '@restart/hooks/useMountEffect';
import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import CryptoJS from 'crypto-js';
import dayjs from 'dayjs';
import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import CookiePolicy from './CookiePolicy';
import styles from './index.module.scss';

import ContainerFullHeight from '@/components/ui/ContainerFullHeight';
import Checkbox from '@/components/ui/kit/Checkbox';
import { FieldText } from '@/components/ui/kit/Field';
import { CRYPTO_SECRET_KEY, CRYPTO_SALT, CRYPTO_IV } from '@/config';
import { setAuthToken } from '@/helpers/API';
import useAuth from '@/hooks/useAuth';
import { NotifierService as notifier } from '@/libs/Notifier';

const cx = classNames.bind(styles);

const Page = () => {
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState({
    loginId: '',
    password: '',
  });
  const [isRememberId, setRememberId] = useState(false);
  const [existSession, setExistSession] = useState();
  const loginIdRef = useRef();
  const passwordRef = useRef();

  const { doLogin, doLoginWithTicket } = useAuth();

  useMountEffect(() => {
    const nextUserInfo = getUserInfo();
    if (nextUserInfo) {
      setUserInfo(nextUserInfo);
      return;
    }

    const loginId = localStorage.getItem('@loginId');
    // 아이디 저장된 경우
    if (loginId) {
      setUserInfo({ ...userInfo, loginId });
      setRememberId(true);

      // 비밀번호 입력 필드 포커스
      passwordRef.current.focus();
      return;
    }
    // 아이디 입력 필드 포커스
    loginIdRef.current.focus();
  });

  useUpdateEffect(() => {
    if (!existSession) return;

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [existSession]);

  const getUserInfo = () => {
    const ciphertext = window.location.search.slice(1);
    if (!ciphertext) return;

    const salt = CryptoJS.enc.Utf8.parse(CRYPTO_SALT);
    const key = CryptoJS.PBKDF2(CRYPTO_SECRET_KEY, salt, {
      keySize: 256 / 32,
      iterations: 1000,
    });

    const decrypted = CryptoJS.AES.decrypt(ciphertext, key, {
      iv: CryptoJS.enc.Utf8.parse(CRYPTO_IV),
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    }).toString(CryptoJS.enc.Utf8);

    const [loginId, password] = decrypted.split(':');
    return { loginId, password };
  };

  const onKeyDown = (e) => {
    if (e.code === 'Enter') {
      handleLoginWithTicket();
    }
  };

  const handleChange = (e) => {
    setUserInfo({
      ...userInfo,
      [e.target.name]: e.target.value,
    });
  };

  const pressEnter = (e) => {
    if (e.code === 'Enter') {
      switch (e.target.name) {
        case 'loginId':
          passwordRef.current.focus();
          break;

        case 'password':
          handleLogin();
          break;

        default:
          break;
      }
    }
  };

  const toggle = () => {
    setRememberId(!isRememberId);
  };

  // 유효성 검사
  const validAll = () => {
    if (userInfo.loginId.trim() === '') {
      notifier.error('Please enter your email.', { top: 48, duration: 2000 });
      loginIdRef.current.focus();
      return false;
    }
    if (userInfo.password.trim() === '') {
      notifier.error('Please enter your password.', { top: 48, duration: 2000 });
      passwordRef.current.focus();
      return false;
    }
    return true;
  };

  // 로그인
  const handleLogin = () => {
    if (!validAll()) return;

    doLogin(userInfo)
      .then((data) => {
        // 중복 로그인 아닐 시
        if (data.accessToken) {
          successLogin(data.accessToken);
        }
        // 중복 로그인 시
        else if (data.ticket) {
          setExistSession(data);
        }
      })
      .catch((_error) => {
        notifier.error('Please check your email or password.', { top: 48, duration: 2000 });
      });
  };

  // 티켓 로그인
  const handleLoginWithTicket = () => {
    doLoginWithTicket(existSession.ticket)
      .then((data) => successLogin(data.accessToken))
      .catch((_error) => {
        notifier.error('Login failed. Please try again.', { top: 48, duration: 2000 });
      });
  };

  // 로그인 성공 시 처리
  const successLogin = (accessToken) => {
    // 아이디 저장 체크한 경우
    if (isRememberId) {
      localStorage.setItem('@loginId', userInfo.loginId);
    }
    // 아이디 저장 체크 해제한 경우
    else if (localStorage.getItem('@loginId')) {
      localStorage.removeItem('@loginId');
    }

    setAuthToken(accessToken);
    navigate('/', { replace: true });
  };

  const reset = () => {
    setExistSession();
  };

  return (
    <ContainerFullHeight className={cx('container')}>
      <form className={cx('form')}>
        <div className={cx(['limiter', { hidden: existSession }])}>
          <FieldText
            ref={loginIdRef}
            name="loginId"
            value={userInfo.loginId}
            placeholder="User ID (Email)"
            onChange={handleChange}
            onKeyDown={pressEnter}
            className={cx('field')}
            autoComplete="username"
          />
          <FieldText
            ref={passwordRef}
            name="password"
            type="password"
            value={userInfo.password}
            placeholder="Password"
            onChange={handleChange}
            onKeyDown={pressEnter}
            className={cx('field')}
            autoComplete="current-password"
          />
          <div className={cx('option')}>
            <Checkbox checked={isRememberId} size={24} onClick={toggle} />
            Remember ID
          </div>
          <button type="button" className={cx(['button', 'accent'])} onClick={handleLogin}>
            Login
          </button>
        </div>
        {existSession && (
          <>
            <div className={cx('message')}>
              <small>Last logged in {dayjs(existSession.lastLoggedAt).format('YYYY-MM-DD HH:mm:ss')}</small>
              <br />
              This account is currently online.
              <br />
              <span className={cx('accent')}>Your previous connection will be terminated.</span>
            </div>
            <div className={cx('limiter')}>
              <button type="button" className={cx(['button', 'accent'])} onClick={handleLoginWithTicket}>
                Login
              </button>
              <button type="button" className={cx(['button', 'cancel'])} onClick={reset}>
                Cancel
              </button>
            </div>
          </>
        )}
      </form>
      <CookiePolicy />
    </ContainerFullHeight>
  );
};

export default Page;
